import {
	Calendar,
	CheckCircle2,
	Circle,
	ClipboardList,
	Clock,
	Plus,
	Trash2,
} from "lucide-react";
import type React from "react";
import { useState } from "react";
import type { PartnerTask } from "../types";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Input } from "./ui/input";
import { Progress } from "./ui/progress";

interface PartnerTasksSectionProps {
	tasks?: PartnerTask[];
	readOnly?: boolean;
	onUpdate?: (tasks: PartnerTask[]) => void;
}

function getProgressIcon(progress: PartnerTask["progress"]) {
	switch (progress) {
		case "completed":
			return <CheckCircle2 size={14} className="text-(--color-success-600)" />;
		case "in_progress":
			return <Clock size={14} className="text-(--color-warning-600)" />;
		default:
			return <Circle size={14} className="text-(--color-muted-foreground)" />;
	}
}

function getProgressVariant(
	progress: PartnerTask["progress"],
): "success" | "warning" | "neutral" {
	switch (progress) {
		case "completed":
			return "success";
		case "in_progress":
			return "warning";
		default:
			return "neutral";
	}
}

function getProgressLabel(progress: PartnerTask["progress"]): string {
	switch (progress) {
		case "completed":
			return "Completed";
		case "in_progress":
			return "In Progress";
		default:
			return "Pending";
	}
}

const PartnerTasksSection: React.FC<PartnerTasksSectionProps> = ({
	tasks,
	readOnly = false,
	onUpdate,
}) => {
	const [editIndex, setEditIndex] = useState<number | null>(null);
	const [editTask, setEditTask] = useState<PartnerTask | null>(null);

	if (!tasks || tasks.length === 0) {
		if (readOnly) return null;
		return (
			<Card className="mb-4 p-6">
				<div className="flex items-center justify-between">
					<h3 className="flex items-center gap-2 font-semibold text-(--color-foreground) text-lg">
						<ClipboardList size={18} className="text-(--color-primary)" />
						Partner Tasks
					</h3>
					<Button
						size="sm"
						onClick={() => {
							const newTask: PartnerTask = {
								task_name: "",
								progress: "pending",
							};
							onUpdate?.([newTask]);
							setEditIndex(0);
							setEditTask(newTask);
						}}
					>
						<Plus size={14} />
						Add Task
					</Button>
				</div>
				<p className="mt-3 text-(--color-muted-foreground) text-sm">
					No tasks added yet.
				</p>
			</Card>
		);
	}

	const completed = tasks.filter((t) => t.progress === "completed").length;
	const overallProgress =
		tasks.length > 0 ? Math.round((completed / tasks.length) * 100) : 0;

	const handleSaveEdit = () => {
		if (editIndex == null || !editTask || !editTask.task_name.trim()) return;
		const updated = [...tasks];
		updated[editIndex] = editTask;
		onUpdate?.(updated);
		setEditIndex(null);
		setEditTask(null);
	};

	const handleCancelEdit = () => {
		if (editIndex != null && tasks[editIndex] && !tasks[editIndex].task_name) {
			const updated = tasks.filter((_, i) => i !== editIndex);
			onUpdate?.(updated);
		}
		setEditIndex(null);
		setEditTask(null);
	};

	const handleDelete = (index: number) => {
		const updated = tasks.filter((_, i) => i !== index);
		onUpdate?.(updated);
	};

	const cycleProgress = (index: number) => {
		const task = tasks[index];
		if (!task) return;
		const order: PartnerTask["progress"][] = [
			"pending",
			"in_progress",
			"completed",
		];
		const currentIdx = order.indexOf(task.progress);
		const nextProgress = order[(currentIdx + 1) % order.length];
		if (!nextProgress) return;
		const updated = [...tasks];
		updated[index] = { ...task, progress: nextProgress };
		onUpdate?.(updated);
	};

	return (
		<Card className="mb-4 p-6">
			<div className="mb-4 flex items-center justify-between">
				<h3 className="flex items-center gap-2 font-semibold text-(--color-foreground) text-lg">
					<ClipboardList size={18} className="text-(--color-primary)" />
					Partner Tasks
				</h3>
				<div className="flex items-center gap-3">
					<span className="text-(--color-muted-foreground) text-sm">
						{completed}/{tasks.length} done
					</span>
					{!readOnly && (
						<Button
							size="sm"
							variant="outline"
							onClick={() => {
								const newTask: PartnerTask = {
									task_name: "",
									progress: "pending",
								};
								const updated = [...tasks, newTask];
								onUpdate?.(updated);
								setEditIndex(updated.length - 1);
								setEditTask(newTask);
							}}
						>
							<Plus size={14} />
							Add
						</Button>
					)}
				</div>
			</div>

			{/* Overall progress */}
			<div className="mb-4 rounded-(--radius-sm) border border-(--color-border) p-3">
				<div className="mb-1 flex justify-between text-(--color-muted-foreground) text-xs">
					<span>Overall Progress</span>
					<span>{overallProgress}%</span>
				</div>
				<Progress value={overallProgress} className="h-2" />
			</div>

			{/* Task list */}
			<div className="flex flex-col gap-2">
				{tasks.map((task, index) => {
					const isEditing = editIndex === index;

					if (isEditing && !readOnly) {
						return (
							<div
								key={`task-edit-${index}`}
								className="flex flex-col gap-2 rounded-(--radius-sm) border border-(--color-primary) bg-(--color-muted) p-3"
							>
								<Input
									placeholder="Task name"
									value={editTask?.task_name ?? ""}
									onChange={(e) =>
										setEditTask((prev) =>
											prev ? { ...prev, task_name: e.target.value } : prev,
										)
									}
									autoFocus
								/>
								<div className="flex gap-2">
									<Input
										type="date"
										placeholder="Deadline"
										value={editTask?.deadline ?? ""}
										onChange={(e) =>
											setEditTask((prev) =>
												prev ? { ...prev, deadline: e.target.value } : prev,
											)
										}
										className="flex-1"
									/>
									<Input
										placeholder="DRI"
										value={editTask?.dri ?? ""}
										onChange={(e) =>
											setEditTask((prev) =>
												prev ? { ...prev, dri: e.target.value } : prev,
											)
										}
										className="flex-1"
									/>
								</div>
								<Input
									placeholder="Comments"
									value={editTask?.comments ?? ""}
									onChange={(e) =>
										setEditTask((prev) =>
											prev ? { ...prev, comments: e.target.value } : prev,
										)
									}
								/>
								<div className="flex justify-end gap-2">
									<Button
										size="sm"
										variant="outline"
										onClick={handleCancelEdit}
									>
										Cancel
									</Button>
									<Button
										size="sm"
										onClick={handleSaveEdit}
										disabled={!editTask?.task_name.trim()}
									>
										Save
									</Button>
								</div>
							</div>
						);
					}

					return (
						<div
							key={`task-${index}`}
							className="flex items-center gap-3 rounded-(--radius-sm) border border-(--color-border) bg-(--color-muted) p-3"
						>
							{!readOnly ? (
								<button
									type="button"
									onClick={() => cycleProgress(index)}
									title="Click to cycle status"
									className="shrink-0"
								>
									{getProgressIcon(task.progress)}
								</button>
							) : (
								<span className="shrink-0">
									{getProgressIcon(task.progress)}
								</span>
							)}

							<div className="min-w-0 flex-1">
								<div className="flex items-center gap-2">
									<span
										className={`font-medium text-sm ${
											task.progress === "completed"
												? "text-(--color-muted-foreground) line-through"
												: "text-(--color-foreground)"
										}`}
									>
										{task.task_name}
									</span>
									<Badge
										variant={getProgressVariant(task.progress)}
										className="text-[10px]"
									>
										{getProgressLabel(task.progress)}
									</Badge>
								</div>
								<div className="mt-1 flex flex-wrap items-center gap-3 text-(--color-muted-foreground) text-xs">
									{task.deadline && (
										<span className="flex items-center gap-1">
											<Calendar size={11} />
											{new Date(task.deadline).toLocaleDateString("en-US", {
												month: "short",
												day: "numeric",
											})}
										</span>
									)}
									{task.dri && <span>DRI: {task.dri}</span>}
									{task.comments && (
										<span className="truncate max-w-[200px]">
											{task.comments}
										</span>
									)}
								</div>
							</div>

							{!readOnly && (
								<div className="flex shrink-0 gap-1">
									<Button
										size="sm"
										variant="ghost"
										onClick={() => {
											setEditIndex(index);
											setEditTask({ ...task });
										}}
										className="h-7 w-7 p-0"
									>
										<span className="sr-only">Edit</span>
										<ClipboardList size={13} />
									</Button>
									<Button
										size="sm"
										variant="ghost"
										onClick={() => handleDelete(index)}
										className="h-7 w-7 p-0 text-(--color-error-500) hover:text-(--color-error-700)"
									>
										<span className="sr-only">Delete</span>
										<Trash2 size={13} />
									</Button>
								</div>
							)}
						</div>
					);
				})}
			</div>
		</Card>
	);
};

export default PartnerTasksSection;
